// Archivo: ui/ProductionOrderForm.js (VERSIÓN FINAL CON DESTINO FIJO A PRODUCCIÓN)

import React, { useState, useEffect, useMemo } from 'react';
import { Combobox } from '@headlessui/react';

export default function ProductionOrderForm({ onSave, onCancel, showModal, stock, designs, locations, currentUser, initialData = null }) {
    const [formData, setFormData] = useState({
        sourceProductGroupId: '',
        designId: '',
        designSize: '',
        quantity: 1,
        destinationLocationId: ''
    });
    const [productQuery, setProductQuery] = useState('');
    const [designQuery, setDesignQuery] = useState('');

    const selectedProductGroup = useMemo(() => {
        return stock.find(item => item.conceptualId === formData.sourceProductGroupId);
    }, [formData.sourceProductGroupId, stock]);

    // --- INICIO DE LA CORRECCIÓN ---
    // Se asegura que el destino SIEMPRE sea la bodega de "Producción".
    useEffect(() => {
        if (initialData) {
            // Si estamos editando, mantenemos los datos originales.
            setFormData({
                sourceProductGroupId: initialData.sourceProductGroup.conceptualId,
                designId: initialData.designId,
                designSize: initialData.designSize,
                quantity: initialData.quantity,
                destinationLocationId: initialData.destinationLocationId
            });
        } else {
            // Para cualquier nueva orden, se busca y se establece "Producción" como destino fijo.
            const productionLocation = locations.find(loc => loc.name.toLowerCase() === 'producción');
            if (productionLocation) {
                setFormData(prev => ({ ...prev, destinationLocationId: productionLocation.id }));
            }
        }
    }, [initialData, locations]);
    // --- FIN DE LA CORRECCIÓN ---

    const filteredPlainStock =
        productQuery === ''
            ? stock
            : stock.filter((item) => {
                return item.conceptualName.toLowerCase().replace(/\s+/g, '').includes(productQuery.toLowerCase().replace(/\s+/g, ''));
            });

    const filteredDesigns =
        designQuery === ''
            ? designs
            : designs.filter((design) =>
                design.name.toLowerCase().replace(/\s+/g, '').includes(designQuery.toLowerCase().replace(/\s+/g, ''))
            );

    const handleSubmit = (e) => {
        e.preventDefault();
        const quantity = parseInt(formData.quantity, 10);
        if (!selectedProductGroup || !formData.designId || !formData.destinationLocationId) {
            showModal("Error", "Debe seleccionar producto de origen y diseño. El destino se establece automáticamente.");
            return;
        }
        if (quantity > selectedProductGroup.totalQuantity) {
            showModal("Error de Stock", `No se puede producir más de la cantidad disponible (${selectedProductGroup.totalQuantity}).`);
            return;
        }
        const design = designs.find(d => d.id === formData.designId);
        if (!design.sizes.includes(selectedProductGroup.size)) {
            showModal("Error de Talla", `El diseño "${design.name}" no es compatible con la talla de producto ${selectedProductGroup.size}.`);
            return;
        }
        onSave({
            ...formData,
            quantity: quantity,
            sourceProductGroup: selectedProductGroup,
        });
        if (!initialData) {
            setFormData(prev => ({
                ...prev,
                sourceProductGroupId: '',
                designId: '',
                designSize: '',
                quantity: 1,
            }));
        }
    };
    
    // Obtenemos el nombre de la ubicación de destino para mostrarlo en el campo deshabilitado.
    const destinationLocationName = useMemo(() => {
        if (!formData.destinationLocationId || !locations.length) return "Cargando...";
        const location = locations.find(loc => loc.id === formData.destinationLocationId);
        return location ? location.name : "Destino no encontrado";
    }, [formData.destinationLocationId, locations]);

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <Combobox 
                    value={formData.sourceProductGroupId} 
                    onChange={(value) => {
                        const product = stock.find(item => item.conceptualId === value);
                        setFormData({...formData, sourceProductGroupId: value, designSize: product?.size || '' });
                    }}
                    disabled={!!initialData}
                >
                    <Combobox.Label className="block text-sm font-medium text-gray-700">Producto Liso a Utilizar (Agrupado)</Combobox.Label>
                    <div className="relative mt-1">
                        <Combobox.Input
                            className="input-field mt-1 bg-white disabled:bg-gray-100 w-full"
                            onChange={(event) => setProductQuery(event.target.value)}
                            displayValue={(conceptualId) => stock.find(i => i.conceptualId === conceptualId)?.conceptualName || ''}
                            placeholder="-- Busca un producto --"
                            onClick={(event) => event.target.select()}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a.75.75 0 01.53.22l3.5 3.5a.75.75 0 01-1.06 1.06L10 4.81 7.03 7.78a.75.75 0 01-1.06-1.06l3.5-3.5A.75.75 0 0110 3zm-3.78 9.28a.75.75 0 011.06 0L10 15.19l2.97-2.91a.75.75 0 111.06 1.06l-3.5 3.5a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 010-1.06z" clipRule="evenodd" /></svg>
                        </Combobox.Button>
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {filteredPlainStock.map((item) => (
                                <Combobox.Option key={item.conceptualId} value={item.conceptualId} className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${ active ? 'bg-cyan-600 text-white' : 'text-gray-900' }`}>
                                    {({ selected }) => (
                                        <span className={`block ${ selected ? 'font-semibold' : '' }`}>
                                            {item.conceptualName} (Disp: {item.totalQuantity})
                                        </span>
                                    )}
                                </Combobox.Option>
                            ))}
                        </Combobox.Options>
                    </div>
                </Combobox>
            </div>
            
            <div>
                 <Combobox 
                    value={formData.designId} 
                    onChange={(value) => setFormData({...formData, designId: value})}
                >
                    <Combobox.Label className="block text-sm font-medium text-gray-700">Diseño a Aplicar</Combobox.Label>
                    <div className="relative mt-1">
                        <Combobox.Input
                            className="input-field mt-1 bg-white w-full"
                            onChange={(event) => setDesignQuery(event.target.value)}
                            displayValue={(designId) => designs.find(d => d.id === designId)?.name || ''}
                            placeholder="-- Busca un diseño --"
                            onClick={(event) => event.target.select()}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a.75.75 0 01.53.22l3.5 3.5a.75.75 0 01-1.06 1.06L10 4.81 7.03 7.78a.75.75 0 01-1.06-1.06l3.5-3.5A.75.75 0 0110 3zm-3.78 9.28a.75.75 0 011.06 0L10 15.19l2.97-2.91a.75.75 0 111.06 1.06l-3.5 3.5a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 010-1.06z" clipRule="evenodd" /></svg>
                        </Combobox.Button>
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {filteredDesigns.map((design) => (
                                <Combobox.Option key={design.id} value={design.id} className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${ active ? 'bg-cyan-600 text-white' : 'text-gray-900' }`}>
                                    {({ selected }) => (<span className={`block ${ selected ? 'font-semibold' : '' }`}>{design.name}</span>)}
                                </Combobox.Option>
                            ))}
                        </Combobox.Options>
                    </div>
                </Combobox>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Cantidad a Producir</label>
                    <input type="number" name="quantity" min="1" max={selectedProductGroup?.totalQuantity || 1} value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value, 10)})} required disabled={!selectedProductGroup} className="input-field mt-1"/>
                </div>
                {/* --- INICIO DE LA CORRECCIÓN --- */}
                {/* Se reemplaza el <select> por un campo de texto deshabilitado para forzar el destino. */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Bodega de Destino (Automático)</label>
                    <input
                        type="text"
                        value={destinationLocationName}
                        disabled
                        className="input-field mt-1 bg-gray-200 text-gray-500 cursor-not-allowed"
                    />
                </div>
                {/* --- FIN DE LA CORRECCIÓN --- */}
            </div>

            <div className="flex justify-end pt-4 border-t">
                <button type="button" onClick={onCancel} className="btn-secondary mr-3">Cancelar</button>
                <button type="submit" className="btn-primary">
                    {initialData ? 'Actualizar Orden' : 'Añadir al Lote'}
                </button>
            </div>
        </form>
    );
}